import { useState, useEffect } from 'react';
import { useCheckout } from '../../../hooks/useCheckout';

const beforeAfterBody = {
  before: 'https://static.readdy.ai/image/388360cd24ebfe3438e68a0ccf7fe406/5540043e75a4496a39b56ba7a23282ab.png',
  after: 'https://static.readdy.ai/image/388360cd24ebfe3438e68a0ccf7fe406/32443206f1de05fae87c4486daae2dfb.png',
};

const fitnessProduct = {
  id: 1,
  badge: 'Fitness Program',
  name: 'The Over-60 Strength Blueprint',
  tagline: 'Build muscle. Lose weight. Protect your joints.',
  description: 'A 12-week, step-by-step strength plan built for men over 60. Every workout is designed around how your body actually moves at this age — no injury risk, no guesswork, just steady results.',
  price: 19.99,
  originalPrice: 24.99,
  beforeAfter: beforeAfterBody,
  beforeLabel: 'Age 60 — 240 lbs',
  afterLabel: 'Age 62 — 185 lbs',
  includes: [
    { icon: 'ri-calendar-check-line', text: '12-week progressive workout plan' },
    { icon: 'ri-video-line', text: '40+ exercise demonstrations' },
    { icon: 'ri-heart-pulse-line', text: 'Joint-safe movement options' },
    { icon: 'ri-bar-chart-line', text: 'Weekly progress tracking' },
    { icon: 'ri-refresh-line', text: 'Recovery & mobility protocols' },
  ],
  detailedFeatures: [
    { title: 'Week-by-Week Workouts', description: 'Every workout for 12 weeks, laid out day by day. No guessing — just follow the plan.', icon: 'ri-calendar-2-line' },
    { title: 'Exercise Video Library', description: 'Over 40 video demos showing proper form and breathing for every movement.', icon: 'ri-play-circle-line' },
    { title: 'Joint-Safe Options', description: 'Alternative exercises for bad knees, shoulders, or back. Train hard without the pain.', icon: 'ri-shield-check-line' },
    { title: 'Progress Tracking', description: 'Simple sheets to log your weights, reps, and measurements week by week.', icon: 'ri-line-chart-line' },
    { title: 'Recovery Protocols', description: 'Stretching routines and rest day guidelines to keep you injury-free.', icon: 'ri-heart-pulse-line' },
    { title: 'Nutrition Guidelines', description: 'Simple protein and calorie targets to fuel muscle growth and fat loss.', icon: 'ri-restaurant-line' },
  ],
  testimonial: { text: "I thought my lifting days were over. This plan proved me wrong. Down 30 lbs, stronger than I've been in 20 years.", author: 'David R., 64' },
};

type Product = typeof fitnessProduct;

function BeforeAfterImages({ product, objectPos = 'center 5%' }: { product: Product; objectPos?: string }) {
  return (
    <div className="relative w-full flex overflow-hidden flex-shrink-0 h-72 sm:h-[420px]">
      <div className="relative w-1/2 h-full overflow-hidden">
        <img
          src={product.beforeAfter.before}
          alt="Before strength training for men over 60 — Mason Hale age 60, 240 lbs, pre-diabetic"
          title="Before Over-60 Strength Blueprint"
          width="600"
          height="800"
          className="w-full h-full object-cover"
          style={{ objectPosition: objectPos }}
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        <div className="absolute bottom-3 left-3">
          <span className="text-white text-[10px] font-black tracking-[0.15em] uppercase bg-black/65 border border-white/10 px-2 py-1 rounded-sm block mb-1">Before</span>
          <div className="text-gray-300 text-[10px] font-semibold">{product.beforeLabel}</div>
        </div>
      </div>

      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#c8a96e]/70 z-10" style={{ transform: 'translateX(-50%)' }} />
      <div
        className="absolute z-20 w-9 h-9 rounded-full bg-[#c8a96e] flex items-center justify-center"
        style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)', boxShadow: '0 0 18px 5px rgba(200,169,110,0.55)' }}
      >
        <i className="ri-arrow-left-right-line text-black text-sm" />
      </div>

      <div className="relative w-1/2 h-full overflow-hidden">
        <img
          src={product.beforeAfter.after}
          alt="After strength training for men over 60 — Mason Hale age 62, 185 lbs, off all medications, muscle building over 60 results"
          title="After Over-60 Strength Blueprint — Age 62, 185 lbs"
          width="600"
          height="800"
          className="w-full h-full object-cover"
          style={{ objectPosition: objectPos }}
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        <div className="absolute bottom-3 right-3 text-right">
          <span className="text-black text-[10px] font-black tracking-[0.15em] uppercase bg-[#c8a96e] px-2 py-1 rounded-sm block mb-1">After</span>
          <div className="text-[#c8a96e] text-[10px] font-semibold">{product.afterLabel}</div>
        </div>
      </div>
    </div>
  );
}

function ProductModal({ product, onClose }: { product: Product; onClose: () => void }) {
  const { createCheckoutSession, loading, error } = useCheckout();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handlePurchase = () => {
    createCheckoutSession('fitness');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
      style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-[#0d0d0d] border border-[#c8a96e]/30 rounded-sm w-full max-h-[92vh] overflow-y-auto"
        style={{ maxWidth: '860px', boxShadow: '0 0 60px rgba(200,169,110,0.18), 0 30px 80px rgba(0,0,0,0.6)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-px w-full" style={{ background: 'linear-gradient(to right, transparent, #c8a96e 40%, #c8a96e 60%, transparent)' }} />

        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 rounded-sm transition-all duration-200 cursor-pointer z-30"
        >
          <i className="ri-close-line text-white text-lg" />
        </button>

        <BeforeAfterImages product={product} objectPos="center top" />

        <div className="p-6 sm:p-8 lg:p-10">
          <span className="inline-block text-[#c8a96e] text-xs font-black tracking-[0.25em] uppercase bg-[#c8a96e]/10 border border-[#c8a96e]/20 px-3 py-1 rounded-sm mb-5">
            {product.badge}
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white leading-tight mb-3">{product.name}</h2>
          <p className="text-[#c8a96e] text-base font-semibold mb-4 leading-snug">{product.tagline}</p>
          <p className="text-gray-400 text-base leading-relaxed mb-8">{product.description}</p>

          <h3 className="text-gray-500 text-xs font-black tracking-[0.2em] uppercase mb-5">What You Get</h3>
          <div className="grid sm:grid-cols-2 gap-3 mb-8">
            {product.detailedFeatures.map((feature, idx) => (
              <div key={idx} className="bg-white/3 border border-white/8 rounded-sm p-4 hover:border-[#c8a96e]/25 transition-colors duration-300">
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-[#c8a96e]/10 border border-[#c8a96e]/20 rounded-sm">
                    <i className={`${feature.icon} text-[#c8a96e] text-sm`} />
                  </span>
                  <div>
                    <h4 className="text-white text-sm font-bold mb-1">{feature.title}</h4>
                    <p className="text-gray-500 text-xs leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white/3 border border-white/8 rounded-sm p-5 mb-8">
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-double-quotes-l text-[#c8a96e] text-xl" />
              </span>
              <div>
                <p className="text-gray-300 text-sm leading-relaxed mb-2 italic">"{product.testimonial.text}"</p>
                <div className="text-[#c8a96e] text-xs font-bold">— {product.testimonial.author}</div>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
              <div className="flex items-start gap-3">
                <i className="ri-error-warning-line text-red-400 text-lg flex-shrink-0" />
                <div>
                  <div className="text-red-400 text-sm font-semibold mb-1">Payment Error</div>
                  <div className="text-red-300 text-xs">{error}</div>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 border-t border-white/8 pt-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-gray-600 text-xs">One-time purchase · Instant access</span>
                <span className="bg-red-500 text-white text-[10px] font-black tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm">On Sale</span>
              </div>
              <div className="flex items-baseline gap-3">
                <div className="text-4xl sm:text-5xl font-black text-[#c8a96e]">${product.price}</div>
                <div className="text-gray-600 text-xl font-semibold line-through">${product.originalPrice}</div>
              </div>
            </div>
            <button
              onClick={handlePurchase}
              disabled={loading}
              aria-label={`Purchase ${product.name} for $${product.price} — on sale from $${product.originalPrice}`}
              className="w-full sm:w-auto px-8 py-4 bg-[#c8a96e] text-black text-sm font-black tracking-[0.15em] uppercase rounded-sm hover:bg-[#d9bc82] active:scale-95 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#c8a96e] disabled:active:scale-100 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line text-base animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                'Start Getting In Shape — $19.99'
              )}
            </button>
          </div>

          <div className="flex items-center justify-center gap-2 mt-5 text-gray-700 text-xs">
            <i className="ri-lock-line text-xs" />
            <span>Secure checkout · Instant access</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Services() {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { createCheckoutSession, loading, error } = useCheckout();

  const handlePurchase = () => {
    createCheckoutSession('fitness');
  };

  return (
    <section id="programs" className="py-20 sm:py-28 bg-[#111111]">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">

        {/* Urgency banner */}
        <div className="flex items-center justify-center gap-3 mb-10 py-3 px-5 rounded-sm border border-[#c8a96e]/30 bg-[#c8a96e]/5">
          <span className="w-2 h-2 rounded-full bg-[#c8a96e] flex-shrink-0 animate-pulse" style={{ boxShadow: '0 0 8px 2px rgba(200,169,110,0.7)' }} />
          <span className="text-[#c8a96e] text-xs sm:text-sm font-bold tracking-wide text-center">
            Introductory pricing — this program will increase in price soon. Lock in your access today.
          </span>
          <span className="w-2 h-2 rounded-full bg-[#c8a96e] flex-shrink-0 animate-pulse" style={{ boxShadow: '0 0 8px 2px rgba(200,169,110,0.7)' }} />
        </div>

        {/* Section label */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-px bg-[#c8a96e]" />
          <span className="text-[#c8a96e] text-xs font-black tracking-[0.3em] uppercase">The Program</span>
        </div>

        {/* Heading */}
        <div className="mb-10 sm:mb-14 max-w-2xl">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-4">
            One Program.<br />Real Results.
          </h2>
          <p className="text-gray-400 text-base sm:text-lg leading-relaxed">
            The exact 12-week strength plan Mason used to go from 240 lbs and on 3 medications — to 185 lbs, off all meds, and stronger than he'd been in decades.
          </p>
        </div>

        {/* Trust bar */}
        <div className="flex flex-wrap items-center gap-4 sm:gap-8 mb-10 sm:mb-14 pb-10 sm:pb-14 border-b border-white/5">
          {[
            { icon: 'ri-lock-line', text: 'Secure Checkout' },
            { icon: 'ri-download-line', text: 'Instant Access' },
            { icon: 'ri-time-line', text: 'One-Time Purchase' },
            { icon: 'ri-infinity-line', text: 'Lifetime Access' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-[#c8a96e] text-sm`} />
              </span>
              <span className="text-gray-500 text-xs font-semibold tracking-wide">{item.text}</span>
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
            <div className="flex items-start gap-3">
              <i className="ri-error-warning-line text-red-400 text-lg flex-shrink-0" />
              <div>
                <div className="text-red-400 text-sm font-semibold mb-1">Payment Error</div>
                <div className="text-red-300 text-xs">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Single Product Card */}
        <article
          itemScope
          itemType="https://schema.org/Product"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className="relative bg-[#0d0d0d] border rounded-sm flex flex-col overflow-hidden"
          style={{
            borderColor: hovered ? 'rgba(200,169,110,0.45)' : 'rgba(255,255,255,0.07)',
            boxShadow: hovered ? '0 0 35px rgba(200,169,110,0.1), 0 16px 50px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.3)',
            transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
            transition: 'border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease',
          }}
          data-product-shop
        >
          <div
            className="h-0.5 w-full flex-shrink-0"
            style={{
              background: hovered ? 'linear-gradient(to right, transparent, #c8a96e, transparent)' : 'transparent',
              transition: 'background 0.3s ease',
            }}
          />

          <BeforeAfterImages product={fitnessProduct} objectPos="center 5%" />

          <div className="p-5 sm:p-8 lg:p-10 flex flex-col flex-1">
            <div className="mb-4">
              <span className="text-[#c8a96e] text-xs font-black tracking-[0.25em] uppercase bg-[#c8a96e]/10 border border-[#c8a96e]/20 px-3 py-1 rounded-sm">
                {fitnessProduct.badge}
              </span>
            </div>

            <h3 itemProp="name" className="text-2xl sm:text-3xl font-black text-white leading-snug mb-2">{fitnessProduct.name}</h3>
            <p className="text-[#c8a96e] text-base font-semibold mb-3 leading-snug">{fitnessProduct.tagline}</p>
            <p itemProp="description" className="text-gray-500 text-sm leading-relaxed mb-8">{fitnessProduct.description}</p>

            {/* What's included */}
            <div className="mb-8">
              <h4 className="text-gray-600 text-xs font-black tracking-[0.2em] uppercase mb-4">What's Inside</h4>
              <ul className="grid sm:grid-cols-2 gap-3">
                {fitnessProduct.includes.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 bg-white/3 border border-white/8 rounded-sm px-4 py-3">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-[#c8a96e] text-sm`} />
                    </span>
                    <span className="text-gray-300 text-sm">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Testimonial */}
            <div className="bg-white/3 border border-white/8 rounded-sm p-5 mb-8">
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-double-quotes-l text-[#c8a96e] text-xl" />
                </span>
                <div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-2 italic">"{fitnessProduct.testimonial.text}"</p>
                  <div className="text-[#c8a96e] text-xs font-bold">— {fitnessProduct.testimonial.author}</div>
                </div>
              </div>
            </div>

            {/* Price + CTA */}
            <div className="border-t border-white/5 pt-6 space-y-3">
              <div className="flex items-center justify-between">
                <div itemProp="offers" itemScope itemType="https://schema.org/Offer">
                  <meta itemProp="priceCurrency" content="USD" />
                  <meta itemProp="price" content={fitnessProduct.price.toString()} />
                  <meta itemProp="availability" content="https://schema.org/InStock" />
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-gray-600 text-xs">One-time purchase</span>
                    <span className="bg-red-500 text-white text-[10px] font-black tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm">On Sale</span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <div className="text-4xl font-black text-[#c8a96e]">${fitnessProduct.price}</div>
                    <div className="text-gray-600 text-lg font-semibold line-through">${fitnessProduct.originalPrice}</div>
                  </div>
                </div>
              </div>
              <button
                onClick={handlePurchase}
                disabled={loading}
                aria-label="Purchase The Over-60 Strength Blueprint for $19.99 — on sale from $24.99"
                className="w-full py-4 bg-[#c8a96e] text-black text-sm font-black tracking-[0.15em] uppercase rounded-sm hover:bg-[#d9bc82] active:scale-95 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#c8a96e] disabled:active:scale-100 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line text-base animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  'Start Getting In Shape — $19.99'
                )}
              </button>
              <button
                onClick={() => setShowModal(true)}
                className="w-full py-2.5 bg-transparent border border-white/10 text-gray-400 text-xs font-semibold tracking-wide uppercase rounded-sm hover:border-white/20 hover:text-white transition-all duration-200 cursor-pointer"
              >
                View Full Details
              </button>
              <div className="flex items-center justify-center gap-1.5 text-gray-600 text-xs pt-1">
                <i className="ri-lock-line text-xs" />
                <span>Secure checkout · Instant access</span>
              </div>
            </div>
          </div>
        </article>

      </div>

      {showModal && <ProductModal product={fitnessProduct} onClose={() => setShowModal(false)} />}
    </section>
  );
}
